/**
 * AIInlineHint
 * Non-blocking AI hint shown below a check-in textarea. Fires 800ms after the user
 * stops typing, only when text length > 30. Dismissible per draft session.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, X, Loader2 } from 'lucide-react';
import { useCanUseAI } from '../../hooks/useAIConfig';
import { aiCoachService } from '../../services/aiCoach.service';
import { PURPLE, PURPLE_BG, PURPLE_BORDER_RGBA } from './ai-theme';

interface Props {
  okrId: string;
  field: 'accomplishments' | 'next_steps';
  text: string;
  debounceMs?: number;
  minChars?: number;
}

export const AIInlineHint: React.FC<Props> = ({
  okrId,
  field,
  text,
  debounceMs = 800,
  minChars = 30,
}) => {
  const { isEligible, aiEnabled } = useCanUseAI();

  const [hint, setHint] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastFetchedKey = useRef<string>('');

  const trimmed = text.trim();
  const stableKey = useMemo(() => `${field}::${trimmed.length}::${trimmed.slice(-200)}`, [field, trimmed]);

  // Skip the network call when the user can't use AI at all, or when the tenant
  // has AI turned off. Errors from the API are still swallowed (see catch below)
  // as a defensive backstop.
  const aiKnownDisabled = !isEligible || !aiEnabled;

  useEffect(() => {
    if (aiKnownDisabled) return;
    if (dismissed) return;
    if (trimmed.length < minChars) {
      setHint(null);
      return;
    }
    if (stableKey === lastFetchedKey.current) return;

    const handle = setTimeout(async () => {
      lastFetchedKey.current = stableKey;
      setLoading(true);
      try {
        const r = await aiCoachService.getDraftHint({ okrId, field, text: trimmed });
        setHint(r.hint);
      } catch (err) {
        // Silent fail — never block the user. Common case: AI not configured (409) → just no hint.
        const e = err as { response?: { data?: { code?: string } } };
        if (e?.response?.data?.code) {
          setHint(null);
        }
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(handle);
  }, [stableKey, trimmed, field, okrId, debounceMs, minChars, dismissed, aiKnownDisabled]);

  // Reset dismissed state when user materially changes content
  useEffect(() => {
    setDismissed(false);
  }, [trimmed.length < minChars]);

  if (aiKnownDisabled) return null;
  if (dismissed) return null;
  if (!loading && !hint) return null;

  return (
    <div
      className="mt-2 rounded-lg p-2.5 flex gap-2 items-start"
      style={{ background: PURPLE_BG, border: `1px solid ${PURPLE_BORDER_RGBA}` }}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 mt-0.5 shrink-0 animate-spin" style={{ color: PURPLE }} />
      ) : (
        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: PURPLE }} />
      )}
      <div className="text-[13px] leading-relaxed flex-1" style={{ color: '#4a2080' }}>
        {loading ? 'AI is checking your draft…' : hint}
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="opacity-60 hover:opacity-100"
        aria-label="Dismiss hint"
      >
        <X className="w-3.5 h-3.5" style={{ color: PURPLE }} />
      </button>
    </div>
  );
};
